import { MockedResponse } from '@apollo/client/testing';
import type { FixInspirationFeedItem, FixInspirationFeedConnection, KeptInspiration, KeptInspirationConnection, KeptInspirationMutationSuccess, FixInspirationMutationError, PageInfo } from '../types/definitions/vendor/graphql-schema.d';
import { GetInspirationFeedDocument, type GetInspirationFeedQuery, type GetInspirationFeedQueryVariables } from '../utils/hooks/getInspirationFeed';
type MockFeedItem = FixInspirationFeedItem & {
  recommendationId?: string | null;
  recommendationPartition?: string | null;
};
const defaultPageInfo: PageInfo = {
  __typename: 'PageInfo',
  endCursor: null,
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: null
};

// ============================================
// Inspiration Feed (Storybook / static fixtures)
// ============================================

export const mockInspirationFeedItems: MockFeedItem[] = [{
  __typename: 'FixInspirationFeedItem',
  contentId: 'feed-item-1',
  contentUrl: 'https://res.cloudinary.com/stitch-fix/image/upload/w_785/fix-inspiration-service-public/inspiration-image-166a410e-c17d-4a5e-a3f5-89be9380b424.jpg',
  recommendationId: 'recommendation-1',
  recommendationPartition: 'partition-1'
}, {
  __typename: 'FixInspirationFeedItem',
  contentId: 'feed-item-2',
  contentUrl: 'https://res.cloudinary.com/stitch-fix/image/upload/w_785/fix-inspiration-service-public/inspiration-image-2efc513c-773a-4f41-8cd2-fd8dd7d32763.jpg',
  recommendationId: 'recommendation-2',
  recommendationPartition: 'partition-2'
}, {
  __typename: 'FixInspirationFeedItem',
  contentId: 'feed-item-3',
  contentUrl: 'https://res.cloudinary.com/stitch-fix/image/upload/w_785/fix-inspiration-service-public/inspiration-image-2efc513c-773a-4f41-8cd2-fd8dd7d32763.jpg',
  recommendationId: 'recommendation-3',
  recommendationPartition: 'partition-3'
}];
export const mockInspirationFeedConnection: FixInspirationFeedConnection = {
  __typename: 'FixInspirationFeedConnection',
  nodes: mockInspirationFeedItems,
  edges: mockInspirationFeedItems.map((node, index) => ({
    __typename: 'FixInspirationFeedConnectionEdge' as const,
    cursor: `cursor-${index}`,
    node
  })),
  pageInfo: defaultPageInfo
};

// ============================================
// Kept Inspirations
// ============================================

export const mockKeptInspirations: KeptInspiration[] = [{
  __typename: 'KeptInspiration',
  id: 'kept-1',
  contentId: 'content-1',
  contentType: 'IMAGE',
  contentUrl: 'https://res.cloudinary.com/stitch-fix/image/upload/w_785/fix-inspiration-service-public/inspiration-image-166a410e-c17d-4a5e-a3f5-89be9380b424.jpg',
  note: 'Love this casual look with a long title that should be truncated',
  deleted: false
}, {
  __typename: 'KeptInspiration',
  id: 'kept-2',
  contentId: 'content-2',
  contentType: 'IMAGE',
  contentUrl: 'https://res.cloudinary.com/stitch-fix/image/upload/w_785/fix-inspiration-service-public/inspiration-image-2efc513c-773a-4f41-8cd2-fd8dd7d32763.jpg',
  note: null,
  deleted: false
}, {
  __typename: 'KeptInspiration',
  id: 'kept-3',
  contentId: 'content-3',
  contentType: 'IMAGE',
  contentUrl: 'https://res.cloudinary.com/stitch-fix/image/upload/w_785/fix-inspiration-service-public/inspiration-image-2efc513c-773a-4f41-8cd2-fd8dd7d32763.jpg',
  note: 'Great for date night',
  deleted: false
}];
export const mockKeptInspirationsConnection: KeptInspirationConnection = {
  __typename: 'KeptInspirationConnection',
  nodes: mockKeptInspirations,
  edges: mockKeptInspirations.map((node, index) => ({
    __typename: 'KeptInspirationEdge' as const,
    cursor: `cursor-${index}`,
    node
  })),
  pageInfo: defaultPageInfo
};

// ============================================
// Mutation Results
// ============================================

export const mockKeptInspirationMutationSuccess: KeptInspirationMutationSuccess = {
  __typename: 'KeptInspirationMutationSuccess',
  success: true,
  keptInspiration: mockKeptInspirations[0]
};
export const mockKeptInspirationMutationError: FixInspirationMutationError = {
  __typename: 'FixInspirationMutationError',
  success: false,
  code: 'UNEXPECTED_ERROR',
  message: 'Something went wrong'
};

// ============================================
// Test factories (Apollo mocks)
// ============================================

/** Placeholder image URL that works in Storybook; varying aspect ratios for masonry. */
const placeholderImageUrl = (id: string, width: number, height: number) => `https://picsum.photos/seed/${id}/${width}/${height}`;
const aspectByIndex = (index: number) => {
  const variants = [[400, 500], [400, 300], [400, 400], [400, 600], [400, 350]];
  return variants[index % variants.length];
};
export const createFeedItems = (count: number, start = 1): MockFeedItem[] => Array.from({
  length: count
}, (_, index) => {
  const itemNumber = start + index;
  const contentId = `feed-item-${itemNumber}`;
  const [w, h] = aspectByIndex(index);
  return {
    __typename: 'FixInspirationFeedItem' as const,
    contentId,
    contentUrl: placeholderImageUrl(contentId, w, h),
    recommendationId: `recommendation-${itemNumber}`,
    recommendationPartition: `partition-${itemNumber}`
  };
});
export const createGetInspirationFeedMock = ({
  items,
  variables,
  endCursor = null,
  hasNextPage = false,
  startCursor: startCursorOverride
}: {
  items: ReturnType<typeof createFeedItems>;
  variables: GetInspirationFeedQueryVariables;
  endCursor?: string | null;
  hasNextPage?: boolean;
  startCursor?: string | null;
}): MockedResponse<GetInspirationFeedQuery> => ({
  request: {
    query: GetInspirationFeedDocument,
    variables
  },
  result: {
    data: {
      client: {
        __typename: 'Client',
        fixInspirationFeed: {
          __typename: 'FixInspirationFeedConnection',
          nodes: items,
          pageInfo: {
            __typename: 'PageInfo',
            endCursor,
            hasNextPage,
            hasPreviousPage: variables.after != null,
            startCursor: startCursorOverride !== undefined ? startCursorOverride : items[0]?.contentId ?? null
          }
        }
      }
    }
  }
});